<template lang="pug">
  .BaseFormGroup.base-form-group(
    :class="classes",
    ref="wrap",
  )
    label.input-label(:for="!readonly? $_id : null" v-if="label")
      span.input-label__text {{ label }}
      span.required(v-if="isRequired && !hideRequiredMark && !isReadonly") *
      a.linkable(
        v-if="linkable",
        @click.prevent="onLinkClick"
      )
        RegularEditIcon.svg-icon.edit-icon
    acre-tooltip.base-form-group__tooltip(
      v-if="tooltip || $slots['tooltip-content']"
      :content-class="`general-tooltip ${tooltipClass}`",
      :content-width="`${tooltipContentWidth}px`",
      v-bind="$attrs",
      help-cursor
    )
      span(slot="activator")
        SolidInfoCircleIcon.svg-icon.base-form-group__label-icon(v-if="tooltipIcon === 'info'")
        SolidQuestionCircleIcon.svg-icon.base-form-group__label-icon(v-else)
      span(v-html="tooltip")
      slot(name="tooltip-content")
    .additional-info(v-if="$slots.additionalInfo")
      slot(name="additionalInfo")
    transition(name="fade-component")
      component.base-form-group__component(
        :id="$_id",
        :is="$_inputComponent",
        ref="input",
        :name="name",
        v-model="$_model",
        :type="inputType",
        v-bind="$attrs",
        :disabled="isDisabled",
        :readonly="isReadonly",
        :required="required",
        :options="$_options",
        :label="label",
        :no-border="noBorder",
        :placeholder="placeholder || label",
        :error-messages="errorText",
        :model="model",
        v-on="$listeners",
        :text-ellipsis-length="textEllipsisLength"
        :persistent-placeholder="persistentPlaceholder"
        :class="{'is--no-focus-state': !showFocusState}"
        :size="size"
      )
        template(v-for="(_, scopedSlotName) in $scopedSlots" v-slot:[scopedSlotName]="slotData")
          slot(:name="scopedSlotName" v-bind="slotData")
        template(v-for="(_, slotName) in $slots" v-slot:[slotName])
          slot(:name="slotName")
    .base-form-group__description(v-if="$slots.description")
      slot(name="description")
</template>

<script>
import get from 'Utils/lodash.get';
import set from 'Utils/lodash.set';
import { getFieldOptionsByParams, getFieldOptionsByOneOf } from 'Utils/forms';
import { isFieldRequired } from 'Helpers/validations/helpers';
import { isObject } from 'Helpers/utils/common';
import { INPUT_COMPONENT_MAP, INPUT_TYPES_MAP } from 'Config/constans';
import BaseFormModel from 'Helpers/models/BaseFormModel';
import BaseFormInput from 'Components/Base/BaseFormInput';
import RegularEditIcon from 'images/svg-icons/regular-edit.svg?component';
import BaseFormInputSkeleton from 'Components/Base/BaseFormInputSkeleton';
import SolidQuestionCircleIcon from 'images/svg-icons/fa-circle-question-mark.svg?component';
import SolidInfoCircleIcon from 'images/svg-icons/solid-info-circle.svg?component';
import /* webpackChunkName: ‘acreTooltip’ */ 'AcreComponents/AcreTooltip/AcreTooltip.vue3.umd';

const BaseFormSelect = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormSelect' */ 'Components/Base/BaseFormSelect'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormSelectState = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormSelectState' */ 'Components/Base/BaseFormSelectState'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormInputCurrency = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormInputCurrency' */ 'Components/Base/BaseFormInputCurrency'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormDate = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormDate' */ 'Components/Base/BaseFormDate'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormRadio = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormRadio' */ 'Components/Base/BaseFormRadio'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormFileUpload = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormFileUpload' */ 'Components/Base/BaseFormFileUpload'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormSwitch = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormSwitch' */ 'Components/Base/BaseFormSwitch'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormCheckbox = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormCheckbox' */ 'Components/Base/BaseFormCheckbox'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormCheckboxGroup = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormCheckboxGroup' */ 'Components/Base/BaseFormCheckboxGroup'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormWebsite = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormWebsite' */ 'Components/Base/BaseFormWebsite'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormTextarea = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormTextarea' */ 'Components/Base/BaseFormTextarea'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});
const BaseFormInputAddress = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormInputAddress' */ 'Components/Base/BaseFormInputAddress'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});

const BaseFormFileMulti = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormFileMulti' */ 'Components/Base/BaseFormFileMulti'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});

const BaseFormFileSingle = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormFileSingle' */ 'Components/Base/BaseFormFileSingle'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});

const BaseFormInputPhone = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormInputPhone' */ 'Components/Base/BaseFormInputPhone'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});

const BaseFormInputCity = () => ({
  component: import(/* webpackChunkGroup: 'BaseFormInputCity' */ 'Components/Base/BaseFormInputCity'),
  loading: BaseFormInputSkeleton,
  delay: 0,
});

export default {
  name: 'BaseFormGroup',
  components: {
    BaseFormInputSkeleton,
    BaseFormSwitch,
    BaseFormCheckboxGroup,
    BaseFormCheckbox,
    BaseFormFileUpload,
    BaseFormRadio,
    BaseFormDate,
    BaseFormInputCurrency,
    BaseFormSelect,
    BaseFormSelectState,
    BaseFormInput,
    BaseFormWebsite,
    BaseFormTextarea,
    BaseFormFileMulti,
    BaseFormFileSingle,
    RegularEditIcon,
    SolidQuestionCircleIcon,
    BaseFormInputAddress,
    SolidInfoCircleIcon,
    BaseFormInputPhone,
    BaseFormInputCity,
  },
  inject: {
    formLoading: {
      from: 'formLoading',
      default: () => {},
    },
    componentLoadingContainer: {
      from: 'componentLoadingContainer',
      default: () => {},
    },
    formId: {
      from: 'formId',
      default: '',
    },
    blockStatus: {
      from: 'blockStatus',
      default: () => {},
    },
  },
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      default: '',
    },
    id: String,
    label: String,
    placeholder: String,
    linkable: Boolean,
    model: {
      type: BaseFormModel,
      default: () => new BaseFormModel(),
    },
    value: true, // eslint-disable-line vue/require-prop-type-constructor
    type: String,
    required: Boolean,
    hideRequiredMark: Boolean,
    focus: [String, Boolean],
    options: [Array, Object],
    errorMessages: [Array, String],
    default: String,
    inputComponent: Object,
    disabled: Boolean,
    readonly: Boolean,
    noBorder: Boolean,
    skipPreload: Boolean,
    tooltip: String,
    tooltipClass: String,
    closeDelay: Number,
    textEllipsisLength: Number,
    hidden: {
      type: Boolean,
      default: false,
    },
    autofocus: Boolean,
    tooltipIcon: {
      type: String,
      default: 'question',
    },
    tooltipContentWidth: {
      type: Number,
      default: 100,
    },
    persistentPlaceholder: {
      type: Boolean,
      default: true,
    },
    showFocusState: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      validator: value => ['large', 'medium', 'small'].includes(value),
      default: 'large',
    },
  },
  data() {
    return {
      componentLoading: Boolean(this.__prerender),
    };
  },
  computed: {
    $_model: {
      get() {
        if (this.value !== undefined) return this.value;
        const modelValue = get(this.model, this.name);
        if ((modelValue === undefined || modelValue === '') && this.default !== undefined) return this.default;
        return modelValue;
      },
      set(value) {
        if (this.model) set(this.model, this.name, value);
        else this.$emit('input', value);
      },
    },
    $_id() {
      if (this.id) return this.id;
      if (!this.normalizedName) return undefined;
      let id = this.normalizedName;
      if (this.formId) id = `${this.formId}--${id}`;
      return id;
    },
    pathToParams() {
      return this.name.split('.').join('.schema.');
    },
    params() {
      return (
        get(this.model.$params, this.pathToParams)
        || { ...this.$attrs, ...this.$props }
      );
    },
    $_inputComponentToLoad() {
      if (this.inputComponent) return this.inputComponent;
      // If component is for state select, return state component
      if (this.isState) return INPUT_COMPONENT_MAP.state;
      if (this.isWebsite) return INPUT_COMPONENT_MAP.website;
      const type = this.type || this.params.type || 'text';
      return INPUT_COMPONENT_MAP[this.name] || INPUT_COMPONENT_MAP[type] || type;
    },
    $_inputComponent() {
      return this.isPreLoading ? 'BaseFormInputSkeleton' : this.$_inputComponentToLoad;
    },
    isReadonly() {
      return this.readonly || this.blockStatus?.readonly;
    },
    inputType() {
      const type = this.type || this.params.type;
      return INPUT_TYPES_MAP[type] || type;
    },
    errorText() {
      return this.errorMessages
        || (this.model.getFieldError && this.model.getFieldError(this.name));
    },
    isError() {
      return Boolean(this.errorText);
    },
    $_options() {
      if (this.type === 'country') return this.options || getFieldOptionsByOneOf('countries');
      return this.options || getFieldOptionsByParams(this.params);
    },
    isRequired() {
      return this.required || isFieldRequired(get(this, 'params.validate', {}), this.model);
    },
    isCheckbox() {
      return this.inputComponent === 'BaseFormCheckbox';
    },
    isState() {
      return /^(.+\.)?state$/.test(this.name);
    },
    isWebsite() {
      return /^(.+\.)?website/.test(this.name);
    },
    normalizedName() {
      return this.name.replace('.', '-');
    },
    classes() {
      let classes = `form-group_${this.normalizedName} is--field-${this.size} `;
      classes += this.linkable ? 'edit-field no-border ' : '';
      classes += this.isCheckbox ? 'base-form-group--checkbox ' : '';
      classes += this.$_inputComponent === 'BaseFormCheckboxGroup' ? 'base-form-group--checkbox-group ' : '';
      classes += this.isError ? 'base-form-group--error ' : '';
      classes += this.isDisabled ? 'base-form-group--disabled ' : '';
      classes += this.isReadonly ? 'base-form-group--readonly ' : '';
      classes += this.noBorder ? 'no-border ' : '';
      classes += this.$_model && this.noBorder ? 'base-form-group--hide-select__placeholder ' : '';
      classes += this.hidden ? 'hidden ' : '';
      return classes;
    },
    isDisabled() {
      return Boolean(this.disabled || (this.formLoading && this.formLoading.send));
    },
    isPreLoading() {
      return this.componentLoading || (this.formLoading && this.formLoading.pre);
    },
  },
  watch: {
    /**
     * Set focus to current group input if
     * - value is true
     * - value is equal name
     */
    focus: {
      handler: 'setFocusIfRequired',
    },
    // Check if focus required after component loaded
    isPreLoading(isPreLoading) {
      if (!isPreLoading) setTimeout(this.setFocusIfRequired);
    },
    $_inputComponentToLoad: {
      async handler(componentName) {
        // Can be component object, so can't be preload in such way
        // Or skip preload by props
        if (isObject(componentName) || this.skipPreload) {
          this.componentLoading = false;
          return;
        }
        // get is component is already loaded and ready to render
        this.componentLoaded = this.getIsInputComponentLoaded(componentName);
        // if not
        if (!this.componentLoaded) {
          // load component
          await this.loadInputComponent(componentName);
          this.componentLoaded = true;
        }
        this.$nextTick(() => {
          // emit ready when component loaded
          this.$emit('ready');
        });
      },
      immediate: true,
    },
    componentLoading: {
      immediate: true,
      handler(value) {
        this.setComponentLoadingContainer(value);
      },
    },
  },
  mounted() {
    // set default value to model
    const modelValue = get(this.model, this.name);
    if (modelValue !== this.$_model) {
      // it's dosn't work if model is deep
      this.$set(this.model, this.name, this.$_model);
    }
    this.setFocusIfRequired();
  },
  beforeDestroy() {
    // Fix to prevent block all form when field destroy while loading
    this.setComponentLoadingContainer(false);
  },
  methods: {
    /**
     * Set focus to current group input
     * try to handle different type of input
     * click if it select
     * focus if it not select and try to find and call menu
     */
    setFocus() {
      const { wrap } = this.$refs;
      const select = wrap.querySelector('.input-group--select');
      const input = wrap.querySelector('input');
      const menu = wrap.querySelector('.menu__activator');
      if (select) {
        // Wait until page loaded and slided because dropdown list can be at wrong position
        setTimeout(() => select.click(), 500);
      } else if (input) {
        input.focus({ preventScroll: true });
        // call menu, if it exist
        if (menu) {
          setTimeout(() => menu.click(), 500);
        }
      }
    },
    setFocusIfRequired() {
      if (this.focus === true || this.focus === this.name || this.autofocus) this.$nextTick(this.setFocus);
    },
    onLinkClick() {
      this.$emit('click-link', this.name);
    },
    setComponentLoadingContainer(value) {
      if (!this.componentLoadingContainer) return;
      this.$set(this.componentLoadingContainer, this.name, value);
    },
    getIsInputComponentLoaded(componentName) {
      // get component from component list in this component
      const componentContainer = this.$options.components[componentName];
      // this is a trick to know if component is ready to render or need to load
      // compiled is true, when component is loaded globally
      // loading is true when component is already loaded
      return componentContainer._compiled || componentContainer.loading;
    },
    async loadInputComponent(componentName) {
      this.componentLoading = true;
      await import(`Components/Base/${componentName}`);
      this.componentLoading = false;
    },
  },
};
</script>
<style lang="sass" scoped>
@import '~Styles/_variables'
.base-form-group
  margin-bottom: 21px
  position: relative
  @media screen and (max-width: 640px)
    margin-bottom: 17px
  label,
  .input-label
    color: $text_gray
    +font(500)
    font-size: 14px
    display: inline-block
    line-height: 18px
    margin-bottom: 8px
    .required
      color: $support_red
    .v-tooltip-activator,
    .base-form-group__tooltip
      border-bottom: none
      margin-left: 8px
      pointer-events: visible
  &__label-icon
    width: 16px
    height: 16px
    fill: $text_gray
  ::v-deep .v-text-field,
  ::v-deep .v-date-field
    margin-top: 0
    padding-top: 0
    &--outlined
      fieldset
        top: -4px
        background-color: $alabaster
        border-radius: 0
        border-color: transparent
        border-width: 2px
        &:before
          content: ''
          position: absolute
          left: -2px
          bottom: -2px
          width: calc(100% + 4px)
          height: 100%
          border-bottom: solid 1px $tones-gray-50
      &:not(.v-input--is-focused),
      &:not(&.error--text)
        &:hover
          fieldset
            border-color: transparent !important
      &.v-input--is-focused
        fieldset
          border: solid 2px $shakespeare
          &:before
            opacity: 0
    .v-input__slot
      min-height: 40px
      height: 48px
      padding: 0 15px
      input
        padding: 12px 0
        max-height: initial
    &.error--text
      fieldset
        border-color: $support_red
        &:before
          display: none
    &__details
      padding: 0 !important
      min-height: 0
      display: none
      transition: all 0.3s ease-in-out
      .v-messages
        &__message
          font-size: 12px
          +font(400)
          line-height: 17px
          margin-top: 4px
    .v-input__prepend-inner // TODO check it
      margin-top: 0
      height: 48px
      align-items: center
      padding-right: 8px
      position: relative
      z-index: 2
      svg
        fill: $text_gray
        width: 20px
        height: 20px
      .v-input__icon
        width: 17px
        min-width: 17px
        .v-icon
          font-size: 17px
          margin-bottom: 0
          color: $text_gray
    .v-text-field__slot // hide default label
      label
        display: none
    &.v-input--is-disabled
      .v-input__slot
        &:before
          border-image: none !important
          border-color: $support_lightgray
  ::v-deep .v-date-field // hide legend for date input
    fieldset
      top: 0
      legend
        display: none
  // global color for v-input
  ::v-deep .v-input:not(.v-input--is-disabled) input,
  ::v-deep .v-input:not(.v-input--is-disabled) textarea
    color: $primary
    caret-color: $primary
    &::placeholder
      color: $input-field-placeholder-color
      +font(400)
    &:-ms-input-placeholder
      color: $input-field-placeholder-color
      +font(400)
  ::v-deep .v-input__append-outer
    height: 48px
    width: 20px
    svg
      width: 20px
      height: 20px
      fill: $text_gray
  ::v-deep .v-input
    &.is--no-focus-state
      fieldset
        border-color: transparent
        &:before
          opacity: 1
  &.no-border // custom text fields no border
    ::v-deep  .error--text
      .v-input__slot
        border-top: transparent !important
        border-left: transparent !important
        border-right: transparent !important
    ::v-deep .v-input__slot
      padding-left: 0 !important
      fieldset
        border-top: transparent !important
        border-left: transparent !important
        border-right: transparent !important
  ::v-deep .v-text-field
    &__details
      margin-bottom: 0 !important
  ::v-deep .v-tooltip-activator,
  &__tooltip
    border-bottom: none
    margin-left: 5px
    pointer-events: visible
    --tooltip-content--z-index: 1003
  &.hidden
    display: none
  &--disabled
    opacity: .3 // TODO: implement disabled fields
    label
      color: $text_gray
      span
        color: $text_gray
    ::v-deep .v-input--is-disabled
      pointer-events: none
    ::v-deep .v-select
      &__selections
        *:nth-child(2)
          display: none
    ::v-deep .v-radio
      .v-input--selection-controls__input
        input
          display: none
    &.no-border
      ::v-deep .v-select
        .v-input__icon
          display: none
      ::v-deep .v-select__selection--disabled
        color: $primary !important
  &--error
    ::v-deep .v-text-field__details
      display: flex
    ::v-deep .file-group
      border-color: $support_red !important
    ::v-deep .input-checkbox
      label
        // color: $support_red // ?????
  &--checkbox
    display: flex
    flex-direction: row-reverse
    justify-content: flex-end
    margin-bottom: 20px
    ::v-deep .input-label
      cursor: pointer
  &--readonly
    position: relative
    ::v-deep label
      .linkable
        position: absolute
        right: 0
        top: 4px
        transition: none
        .edit-icon
          fill: transparent
          width: sizeInRem(14)
          height: sizeInRem(14)
          +mt(.2s)
    ::v-deep .v-input
      pointer-events: none
    ::v-deep .v-select
      .v-input__append-inner
        display: none
    ::v-deep .v-input__control
      .v-input__slot
        padding: 0 !important
        fieldset
          border-left: none
          border-right: none
          border-top: none
          border-radius: 0px
          padding-left: 0
          background-color: transparent
    &:hover
      ::v-deep label
        .linkable
          .edit-icon
            width: sizeInRem(14)
            height: sizeInRem(14)
            fill: rgba($text_gray, 0.8)
            +mt(.2s)
            &:hover
              fill: rgba($text_gray, 1)
      ::v-deep .v-input__slot
        fieldset
          border-radius: 0
    ::v-deep .v-input__append-outer
      opacity: 0
    ::v-deep .file-upload
      .left-wrap
        .base-btn
          background-color: $support_lightgray !important
          cursor: auto !important
          border: 1px solid $text_gray !important
          &:hover
            border: 1px solid $text_gray !important
      .with-icon
        a
          display: none
        .file-name
          color: $primary
  &--hide-select__placeholder // if input have data but disabled
    ::v-deep .v-select
      input
        &::placeholder
          -webkit-text-fill-color: transparent
  &.edit-field
    ::v-deep .v-select__slot
      .v-input__append-inner
        display: none
  // gengeral
  ::v-deep input[disabled],
  ::v-deep textarea
    opacity: 1
    color: $primary !important
    -webkit-text-fill-color: $primary
    &::placeholder
      -webkit-text-fill-color: $input-field-placeholder-color
  ::v-deep input::placeholder,
  ::v-deep textarea::placeholder
    color: $input-field-placeholder-color
    opacity: 1
    +font(300)
  // remove arrows for input type "number"
  ::v-deep input[type="number"]::-webkit-outer-spin-button,
  ::v-deep input[type="number"]::-webkit-inner-spin-button
    -webkit-appearance: none
    margin: 0
  ::v-deep input[type="number"]
    -moz-appearance: textfield
  ::v-deep input[type="number"]:hover,
  ::v-deep input[type="number"]:focus
    -moz-appearance: number-input
  ::v-deep .v-input
    font-size: 16px
    +font(400)
    line-height: 22px
    &.theme--light
      background-color: transparent !important
    &__slot
      margin-bottom: 0
  ::v-deep input
    text-overflow: ellipsis
    white-space: nowrap
    overflow: hidden
  &__description
    color: $text_gray
    font-size: 12px
    line-height: 17px
    margin-top: 4px
    +font(400)
  &.is--field-medium
    ::v-deep .v-input__slot
      height: 40px
    ::v-deep .v-input__prepend-inner
      height: 40px
    ::v-deep .v-input__append-outer
      height: 40px
</style>
